import { AddressDto } from 'src/api/octane/models.homes';
import { hasConsent } from 'src/features/consent/consent-state';
import { queuePixelEvent } from 'src/features/consent/utils';

export const GA_ACTIONS = {
  ADDRESS_ENTERED_CALCULATOR: 'AddressEnteredCalculator',
  ADDRESS_ENTERED_LANDING_PAGE_COVER: 'AddressEnteredLandingPageCover',
  ADDRESS_ENTERED_SIGNUP_FLOW: 'AddressEnteredSignUpFlow',
  LEAD_CREATED_CALCULATOR: 'LeadCreatedCalculator',
  LEAD_CREATED_SIGNUP_FLOW: 'LeadCreatedSignUpFlow',
  LEAD_CREATED_TRY_THE_PROS: 'LeadCreatedTryThePros',
  LEAD_CREATED_LEASING: 'LeadCreatedLeasing',
  LEAD_CREATED_RENT_ESTIMATE: 'LeadCreatedRentEstimate',
};

const ACTIONS_MAP = {
  LeadCreatedCalculator: 'calculator',
  LeadCreatedSignUpFlow: 'sign_up_flow',
  LeadCreatedTryThePros: 'ask_a_question',
  LeadCreatedLeasing: 'leasing',
  LeadCreatedRentEstimate: 'rent_estimate',
};

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const pageview = (url: string) => {
  if (window.gtag) {
    window.gtag('config', process.env.NEXT_PUBLIC_GA4_ID, {
      page_path: url,
    });
  }
};

// https://developers.google.com/analytics/devguides/collection/gtagjs/events

type EnhancedConversionUser = {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  zipcode?: string;
  country?: string;
};

export const setEnhancedConvertionData = (user: EnhancedConversionUser) => {
  const enhancedConversionUser: any = {};
  if (user.email) enhancedConversionUser.email = user.email;
  if (user.phone) enhancedConversionUser.phone_number = user.phone;
  if (user.firstName) enhancedConversionUser.first_name = user.firstName;
  if (user.lastName) enhancedConversionUser.last_name = user.lastName;
  if (user.zipcode) enhancedConversionUser.postal_code = user.zipcode;
  if (user.country) enhancedConversionUser.country = user.country;

  const send = () => window.dataLayer?.push?.(enhancedConversionUser);

  if (hasConsent('marketing')) {
    send();
  } else {
    queuePixelEvent({ vendor: 'enhanced-conversions', send });
  }
};

const withoutAddress = ({
  streetAddress: _streetAddress,
  city: _city,
  zipcode: _zipcode,
  rentEstimate: _rentEstimate,
  ...rest
}: Record<string, unknown>) => rest;

export const trackHomeownerAddressEntered = (
  action: any,
  address: any,
  rent?: any,
) => {
  if (!action || !address?.city) {
    return;
  }

  const addressEnteredEvent: any = {
    event_category: 'Homeowner',
    event_label: `Rent|${address.city.replace(/ /g, '')}-${address.state.toUpperCase()}|${address.zipcode}`,
  };

  if (rent?.estimate) {
    addressEnteredEvent.value = parseInt(rent.estimate, 10);
  }

  if (window.gtag) {
    window.gtag('event', action, addressEnteredEvent);
  }
};

export const trackHomeownerLeadCreated = ({
  action,
  address,
  rent,
  coverageType,
  region,
}: {
  action: any;
  address: any;
  rent: any;
  coverageType?: string;
  region?: string;
}) => {
  if (!action) {
    return;
  }

  const leadCreatedEvent: any = { event_category: 'Homeowner' };
  const datadogAndMixPanelLeadEvent: any = {
    type: ACTIONS_MAP[action as keyof typeof ACTIONS_MAP],
  };

  if (region) {
    datadogAndMixPanelLeadEvent.region = region;
  }

  if (address?.zipcode) {
    leadCreatedEvent.zipcode = address.zipcode;
    datadogAndMixPanelLeadEvent.zipcode = address.zipcode;
  }

  if (address?.streetAddress) {
    datadogAndMixPanelLeadEvent.streetAddress = address.streetAddress;
  }

  if (address?.city) {
    leadCreatedEvent.event_label = `Rent|${address.city.replace(/ /g, '')}-${address.state.toUpperCase()}|${
      address.zipcode
    }`;

    datadogAndMixPanelLeadEvent.city = address.city;
  }

  if (rent?.estimate) {
    leadCreatedEvent.value = parseInt(rent.estimate, 10);

    datadogAndMixPanelLeadEvent.rentEstimate = rent?.estimate;
  }

  if (coverageType) {
    leadCreatedEvent.coverage_type = coverageType;
    datadogAndMixPanelLeadEvent.coverage_type = coverageType;
  }

  try {
    // TODO: Find a better way to structure this.
    if (window.mixpanel) {
      window.mixpanel.track('lead_created', datadogAndMixPanelLeadEvent);
    }

    if (window.DD_RUM) {
      window.DD_RUM.addAction(
        'lead_created',
        withoutAddress(datadogAndMixPanelLeadEvent),
      );
    }

    if (coverageType !== 'out_of_coverage') {
      window.dataLayer?.push({
        event: action,
        ...leadCreatedEvent,
      });
    }
  } catch (e) {
    console.log(e);
  }
};

export function trackHomeownerXLeadCreated(
  address: AddressDto,
  source: string,
) {
  const leadCreatedEvent: Record<string, string> = { source };
  const datadogAndMixPanelLeadEvent: Record<string, string> = { source };

  if (address?.zipcode) {
    leadCreatedEvent.zipcode = address.zipcode;
    datadogAndMixPanelLeadEvent.zipcode = address.zipcode;
  }

  if (address?.streetAddress) {
    datadogAndMixPanelLeadEvent.streetAddress = address.streetAddress;
  }

  if (address?.city) {
    leadCreatedEvent.city = address.city;
    datadogAndMixPanelLeadEvent.city = address.city;
  }

  try {
    if (window.mixpanel) {
      window.mixpanel.track('lead_created_x', datadogAndMixPanelLeadEvent);
    }

    if (window.DD_RUM) {
      window.DD_RUM.addAction(
        'lead_created_x',
        withoutAddress(datadogAndMixPanelLeadEvent),
      );
    }

    window.dataLayer?.push({
      event: 'lead_created_x',
      ...leadCreatedEvent,
    });
  } catch (e) {
    console.log(e);
  }
}
